// ui/hub.ts — THE THRESHOLD (DESIGN §9).
//
// One screen, no walking simulator: a stone landing before the descent gate.
// Left, the Ember Shrine (whole tree visible from the first visit, locked rows
// priced and named). Right, the Provisioner. Below, the Ledger, the Weapon Rack
// and the stats plaque. At the bottom, the gate.
//
// DOM, not canvas — the run is canvas, the menus are DOM (ARCHITECTURE §1), and
// the simulation is not running while this is up (game.ts steps nothing on the
// hub screen). Keyboard-first the same way the altar overlay is: number keys buy
// the numbered shrine nodes, ENTER descends, W/T cycle the rack, and every
// control is a real <button> so Tab and Space work too.

import { meta } from '../game/meta'
import {
  PROVISIONER_STOCK,
  SHRINE_ROWS,
  TALISMAN_IDS,
  UNLOCK_BY_ID,
} from '../data/unlocks'
import { FEATS } from '../data/feats'
import type { FeatDef, UnlockNode } from '../types'
import { getWeapon, weaponIds } from '../game/weapons'
import { audio } from '../engine/audio'

let root: HTMLElement | null = null
let panel: HTMLElement | null = null
let onDescend: (() => void) | null = null
let keyHandler: ((e: KeyboardEvent) => void) | null = null
/** data-buy ids for the number keys, in the order the shrine renders them. */
let numberTargets: string[] = []

function ui(): HTMLElement {
  if (!root) root = document.getElementById('ui') as HTMLElement
  return root
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V']

function rankLabel(node: UnlockNode, rank: number): string {
  if (node.ranks <= 1) return esc(node.name)
  return `${esc(node.name)} ${ROMAN[Math.min(rank, ROMAN.length - 1)]}`
}

function pips(node: UnlockNode): string {
  if (node.ranks <= 1) return ''
  const rank = meta.shrineRank(node.id)
  let out = ''
  for (let i = 0; i < node.ranks; i++) out += i < rank ? '●' : '○'
  return `<span class="pips">${out}</span>`
}

function fmtTime(ms: number): string {
  if (!ms) return '—'
  const s = Math.round(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

// ---------------------------------------------------------------- the shrine

function shrineNodeHtml(node: UnlockNode, rowOpen: boolean): string {
  const rank = meta.shrineRank(node.id)
  const cost = meta.nextCost(node)
  const maxed = cost < 0
  const affordable = !maxed && meta.data.embers >= cost
  const pairId = meta.pairId(node)
  const active = pairId ? meta.isNodeActive(node) : true
  const owned = rank > 0

  const cls = ['node']
  if (!rowOpen) cls.push('is-locked')
  if (maxed) cls.push('is-max')
  if (owned) cls.push('is-owned')
  if (rowOpen && !maxed && !affordable) cls.push('is-poor')
  if (pairId) cls.push('is-pair')
  if (pairId && owned) cls.push(active ? 'is-active' : 'is-inactive')

  // A pair half that is owned but asleep is a free swap, not a purchase.
  const swap = pairId !== '' && owned && !active
  const buyable = rowOpen && !maxed && affordable && !swap
  let key = ''
  if (buyable) {
    if (numberTargets.length < 9) {
      numberTargets.push('node:' + node.id)
      key = `<span class="node-key">${numberTargets.length}</span>`
    }
  }

  let action = ''
  if (!rowOpen) action = `<span class="node-price locked">${maxed ? '—' : cost + ' embers'}</span>`
  else if (swap) action = '<span class="node-price swap">SWAP TO THIS</span>'
  else if (pairId && owned && active) action = '<span class="node-price active">◆ ACTIVE</span>'
  else if (maxed) action = '<span class="node-price max">FULLY WOKEN</span>'
  else action = `<span class="node-price${affordable ? '' : ' poor'}">${cost} embers</span>`

  const attr = swap
    ? `data-swap="${esc(pairId)}:${meta.side(node)}"`
    : `data-buy="node:${esc(node.id)}"`
  const dead = !rowOpen || (!buyable && !swap)

  return `<button class="${cls.join(' ')}" ${attr} data-node="${esc(node.id)}" ${dead ? 'disabled' : ''}>
      ${key}
      <span class="node-name">${esc(node.name)}${node.ranks > 1 && !maxed ? ` <em>${ROMAN[rank + 1]}</em>` : ''}</span>
      ${pips(node)}
      <span class="node-desc">${esc(node.desc)}</span>
      ${action}
    </button>`
}

function shrineHtml(): string {
  numberTargets = []
  let out = ''
  for (let r = 1; r <= 6; r++) {
    const nodes = SHRINE_ROWS[r - 1] ?? []
    const open = meta.rowOpen(r)
    const note = open ? 'open' : `reach zone ${r} to unveil`
    // Pairs render as one two-sided toggle; DESIGN counts them as one node.
    const done: Record<string, boolean> = {}
    let cards = ''
    for (const node of nodes) {
      const pairId = meta.pairId(node)
      if (pairId) {
        if (done[pairId]) continue
        done[pairId] = true
        const sides = nodes.filter((n) => meta.pairId(n) === pairId)
        cards += `<div class="pair-wrap">
            <div class="pair-mark">EITHER / OR</div>
            ${sides.map((n) => shrineNodeHtml(n, open)).join('')}
          </div>`
        continue
      }
      cards += shrineNodeHtml(node, open)
    }
    out += `<section class="shrine-row${open ? '' : ' is-locked'}" data-row="${r}">
        <div class="row-head"><span class="row-n">ROW ${r}</span><span class="row-note">${note}</span></div>
        <div class="row-nodes">${cards}</div>
      </section>`
  }
  return out
}

// ----------------------------------------------------------- the provisioner

function stockCardHtml(node: UnlockNode): string {
  const owned = meta.ownsStock(node.id)
  const gated = !meta.deedGateMet(node)
  const cost = node.costs[0]
  const poor = meta.data.crests < cost
  // An unlockable weapon form that has no implementation yet must not be sold.
  const weapon = typeof node.params?.weapon === 'string' ? node.params.weapon : ''
  const unforged = weapon !== '' && !weaponIds().includes(weapon)

  const cls = ['stock']
  if (owned) cls.push('is-owned')
  if (gated) cls.push('is-gated')
  if (!owned && !gated && poor) cls.push('is-poor')
  if (unforged) cls.push('is-unforged')

  let foot: string
  if (owned) foot = '<span class="stock-price owned">✦ IN STOCK</span>'
  else if (gated) foot = `<span class="stock-price gated">DEED: ${esc(meta.deedGateName(node))}</span>`
  else if (unforged) foot = '<span class="stock-price gated">NOT YET FORGED</span>'
  else foot = `<span class="stock-price${poor ? ' poor' : ''}">${cost} crest${cost === 1 ? '' : 's'}</span>`

  const dead = owned || gated || poor || unforged
  return `<button class="${cls.join(' ')}" data-buy="stock:${esc(node.id)}" data-stock="${esc(node.id)}" ${dead ? 'disabled' : ''}>
      <span class="stock-name">${esc(node.name)}</span>
      <span class="stock-desc">${esc(node.desc)}</span>
      ${foot}
    </button>`
}

// ----------------------------------------------------------------- the rack

function rackHtml(): string {
  const forms = ['cinderblade', 'whisperbow', 'stormstaff', 'bulwarkmaul']
  const owned = new Set(['cinderblade', ...meta.ownedWeaponStock()])
  const current = meta.data.loadout.weapon
  // ?weapon= override: whatever the run will actually use is shown as chosen.
  owned.add(current)
  const slots = forms
    .map((id) => {
      const registered = weaponIds().includes(id)
      const have = owned.has(id) && registered
      const sel = current === id
      const name = registered ? getWeapon(id).name : stockNameForWeapon(id)
      const cls = ['slot']
      if (sel) cls.push('is-sel')
      if (!have) cls.push('is-locked')
      const note = have ? (sel ? 'EQUIPPED' : 'EQUIP') : registered ? 'LOCKED' : 'NOT FORGED'
      return `<button class="${cls.join(' ')}" data-weapon="${esc(id)}" ${have ? '' : 'disabled'}>
          <span class="slot-name">${esc(name)}</span><span class="slot-note">${note}</span>
        </button>`
    })
    .join('')

  const tal = meta.data.loadout.talisman
  const talSlots = ['', ...TALISMAN_IDS]
    .map((id) => {
      const node = id ? UNLOCK_BY_ID.get(id) : undefined
      const have = id === '' || meta.ownsStock(id)
      const sel = (tal ?? '') === id
      const cls = ['slot', 'tal']
      if (sel) cls.push('is-sel')
      if (!have) cls.push('is-locked')
      return `<button class="${cls.join(' ')}" data-talisman="${esc(id)}" ${have ? '' : 'disabled'}>
          <span class="slot-name">${esc(node ? node.name : 'No talisman')}</span>
          <span class="slot-note">${sel ? 'WORN' : have ? 'WEAR' : 'LOCKED'}</span>
        </button>`
    })
    .join('')

  return `<div class="rack-group"><h4>WEAPON FORM <span class="dim">(W cycles)</span></h4><div class="slots">${slots}</div></div>
    <div class="rack-group"><h4>TALISMAN <span class="dim">(T cycles)</span></h4><div class="slots">${talSlots}</div></div>`
}

function stockNameForWeapon(weaponId: string): string {
  for (const n of PROVISIONER_STOCK) if (n.params?.weapon === weaponId) return n.name
  return weaponId
}

// --------------------------------------------------------------- the ledger

function featRowHtml(f: FeatDef): string {
  const done = meta.featDone(f.id)
  const prog = Math.min(f.target, meta.featProgress(f.id))
  const revealed = meta.featRevealed(f)
  const pct = Math.max(0, Math.min(100, Math.round((prog / f.target) * 100)))
  if (!revealed) {
    return `<div class="feat is-hidden" data-feat="${esc(f.id)}">
        <span class="feat-name">???</span>
        <span class="feat-track"><i style="width:0%"></i></span>
        <span class="feat-v">hidden deed</span>
      </div>`
  }
  return `<div class="feat${done ? ' is-done' : ''}" data-feat="${esc(f.id)}" title="${esc(f.desc)}">
      <span class="feat-name">${esc(f.name)}</span>
      <span class="feat-track"><i style="width:${pct}%"></i></span>
      <span class="feat-v">${done ? `✦ ${f.crests}` : `${prog}/${f.target}`}</span>
    </div>`
}

// ------------------------------------------------------------------- screen

function render(): void {
  if (!panel) return
  const d = meta.data
  const stock = PROVISIONER_STOCK.map(stockCardHtml).join('')
  // shrineHtml() fills numberTargets, so it has to run before the footer text.
  const shrine = shrineHtml()

  panel.innerHTML = `
    <div class="hub-head">
      <div>
        <h1>THE THRESHOLD</h1>
        <p class="sub">Stone landing, cold gate, everything you brought back. Spend it, then go down.</p>
      </div>
      <div class="wallets">
        <div class="wallet"><span>EMBERS</span><b id="hub-embers">${d.embers}</b></div>
        <div class="wallet crest"><span>CRESTS</span><b id="hub-crests">${d.crests}</b></div>
      </div>
    </div>

    <div class="hub-cols">
      <section class="pane" id="hub-shrine">
        <h3 class="pane-h">THE EMBER SHRINE <span class="dim">— rows unveil as you go deeper</span></h3>
        ${shrine}
      </section>

      <section class="pane" id="hub-provisioner">
        <h3 class="pane-h">THE PROVISIONER <span class="dim">— crests only</span></h3>
        <div class="stock-grid">${stock}</div>
      </section>
    </div>

    <div class="hub-strip">
      <section class="pane" id="hub-ledger">
        <h3 class="pane-h">THE LEDGER <span class="dim">— deeds pay crests, even in a losing run</span></h3>
        <div class="feat-list">${FEATS.map(featRowHtml).join('')}</div>
      </section>

      <section class="pane" id="hub-rack">
        <h3 class="pane-h">THE WEAPON RACK</h3>
        ${rackHtml()}
      </section>

      <section class="pane" id="hub-plaque">
        <h3 class="pane-h">THE PLAQUE</h3>
        <div class="plaque-rows">
          <div class="k">Descents</div><div class="v">${d.stats.runs}</div>
          <div class="k">Deaths</div><div class="v">${d.stats.deaths}</div>
          <div class="k">Best depth</div><div class="v">zone ${Math.max(0, d.stats.bestZone)}</div>
          <div class="k">Best time</div><div class="v">${fmtTime(d.stats.bestTimeMs)}</div>
          <div class="k">Kills, all told</div><div class="v">${d.stats.kills}</div>
          <div class="k">Embers, all told</div><div class="v ember">${d.stats.embersLifetime}</div>
        </div>
      </section>
    </div>

    <div class="hub-gate">
      <button class="btn descend" id="hub-descend">DESCEND&nbsp;&nbsp;(ENTER)</button>
      <div class="hub-foot">
        ${numberTargets.length > 0 ? `1–${numberTargets.length} buy the numbered shrine nodes · ` : ''}W weapon · T talisman · ENTER descend
      </div>
      <div class="hub-foot hub-controls">
        IN THE DEEP:&nbsp; WASD move · mouse aim · LMB attack · RMB special · SPACE dash · Q potion · E interact · ESC pause &amp; help
      </div>
    </div>
  `
}

/** Re-render in place and put the focus back where the player left it. */
function refresh(): void {
  const active = document.activeElement as HTMLElement | null
  const key =
    active?.getAttribute('data-node') ??
    active?.getAttribute('data-stock') ??
    (active?.id === 'hub-descend' ? '#descend' : null)
  render()
  if (!panel || !key) return
  const next =
    key === '#descend'
      ? panel.querySelector('#hub-descend')
      : (panel.querySelector(`[data-node="${key}"]`) ?? panel.querySelector(`[data-stock="${key}"]`))
  ;(next as HTMLElement | null)?.focus()
}

function buy(id: string): void {
  audio.unlock()
  if (id.startsWith('node:')) {
    if (meta.buyShrine(id.slice(5))) audio.sfx('pickup')
    else audio.sfx('ui')
  } else if (id.startsWith('stock:')) {
    if (meta.buyStock(id.slice(6))) audio.sfx('pickup')
    else audio.sfx('ui')
  }
  refresh()
}

function cycleWeapon(dir: number): void {
  const owned = ['cinderblade', ...meta.ownedWeaponStock()].filter((w) => weaponIds().includes(w))
  if (owned.length === 0) return
  const i = owned.indexOf(meta.data.loadout.weapon)
  const next = owned[(((i < 0 ? 0 : i + dir) % owned.length) + owned.length) % owned.length]
  meta.setWeapon(next)
  audio.sfx('ui')
  refresh()
}

function cycleTalisman(dir: number): void {
  const owned: (string | null)[] = [null, ...TALISMAN_IDS.filter((t) => meta.ownsStock(t))]
  const i = owned.indexOf(meta.data.loadout.talisman)
  const next = owned[(((i < 0 ? 0 : i + dir) % owned.length) + owned.length) % owned.length]
  meta.setTalisman(next)
  audio.sfx('ui')
  refresh()
}

export function showHub(descend: () => void): void {
  hideHub()
  onDescend = descend
  const el = document.createElement('div')
  el.className = 'screen hub'
  el.id = 'hub'
  ui().appendChild(el)
  panel = el
  render()

  el.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement | null)?.closest('[data-buy],[data-swap],[data-weapon],[data-talisman],#hub-descend')
    if (!t) return
    const b = t as HTMLButtonElement
    if (b.disabled) return
    if (b.id === 'hub-descend') {
      audio.unlock()
      onDescend?.()
      return
    }
    const swap = b.getAttribute('data-swap')
    if (swap) {
      const [pairId, side] = swap.split(':')
      meta.setPairSide(pairId, side === 'b' ? 'b' : 'a')
      audio.sfx('ui')
      refresh()
      return
    }
    const wep = b.getAttribute('data-weapon')
    if (wep !== null) {
      meta.setWeapon(wep)
      audio.sfx('ui')
      refresh()
      return
    }
    const tal = b.getAttribute('data-talisman')
    if (tal !== null) {
      meta.setTalisman(tal === '' ? null : tal)
      audio.sfx('ui')
      refresh()
      return
    }
    const id = b.getAttribute('data-buy')
    if (id) buy(id)
  })

  // Capture phase on document: the engine's keydown listener sits on window in
  // the bubble phase, so stopping here keeps hub typing out of the game input
  // (and undoes its Tab/Space preventDefault, which the hub needs).
  keyHandler = (e: KeyboardEvent): void => {
    if (!panel) return
    const code = e.code
    const m = /^(?:Digit|Numpad)([1-9])$/.exec(code)
    if (m) {
      e.stopPropagation()
      const target = numberTargets[Number(m[1]) - 1]
      if (target) buy(target)
      return
    }
    if (code === 'Enter' || code === 'NumpadEnter') {
      e.stopPropagation()
      audio.unlock()
      onDescend?.()
      return
    }
    if (code === 'KeyW') {
      e.stopPropagation()
      cycleWeapon(e.shiftKey ? -1 : 1)
      return
    }
    if (code === 'KeyT') {
      e.stopPropagation()
      cycleTalisman(e.shiftKey ? -1 : 1)
      return
    }
    if (code === 'Tab' || code === 'Space') {
      // let the browser do its own focus/activate job
      e.stopPropagation()
    }
  }
  document.addEventListener('keydown', keyHandler, true)
}

export function hideHub(): void {
  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler, true)
    keyHandler = null
  }
  if (panel && panel.parentElement) panel.parentElement.removeChild(panel)
  panel = null
  onDescend = null
}

export function hubVisible(): boolean {
  return panel != null
}
